/**
 * quiz-embedded -- Sub-modo embebible dentro de stepper-flow.
 *
 * Renderiza UNA pregunta de quiz con opciones (multiple choice / V-F / texto libre)
 * + feedback inmediato + retry opcional. La pregunta puede venir directamente en
 * stepData.question (modo statico) o ser provista por la BD #/capacitacion via
 * stepData.skillCode (modo dinamico, T4 lo enganchara al QuestionApiClient real).
 *
 * V1 PRIORITY: soporta question pre-llenada. Modo skillCode declara el contrato
 * pero defiere fetch real a T4 (queda como TODO marcado).
 *
 * stepData:
 *   - question     (string, opcional)  - texto pregunta (modo statico)
 *   - options      (array{id,text}, opcional) - opciones MCQ (modo statico)
 *   - correctId    (string, opcional)  - id de la opcion correcta
 *   - explanation  (string, opcional)  - feedback al responder
 *   - skillCode    (string, opcional)  - referencia a malla (T4 fetcheara)
 *   - allowRetry   (boolean, opcional) - default true
 *
 * stepCtx:
 *   - escHtml(s), recordResult(data), goNext()  // habilita avance solo tras respuesta
 *
 * @module presentation/components/ActionAreaModes/quiz-embedded
 */

export function renderQuizEmbedded(container, stepData, stepCtx) {
  const question = stepData?.question || '';
  const options = Array.isArray(stepData?.options) ? stepData.options : [];
  const correctId = stepData?.correctId || null;
  const explanation = stepData?.explanation || '';
  const skillCode = stepData?.skillCode || '';
  const allowRetry = stepData?.allowRetry !== false;

  // Modo skillCode sin question pre-llenada: placeholder hasta T4
  if (skillCode && !question) {
    container.innerHTML = `
      <div class="aa-step-quiz aa-step-quiz--pending" data-aa-substep-type="quiz-embedded"
           style="padding:14px;background:#161b22;border:1px dashed #d29922;border-radius:6px;color:#d29922;font-size:0.875rem">
        <strong>Quiz por skillCode</strong>: <code style="background:#0d1117;padding:1px 6px;border-radius:3px">${stepCtx.escHtml(skillCode)}</code>
        <p style="margin:6px 0 0;color:#8b949e;font-size:0.875rem">
          Fetch dinamico de pregunta queda pendiente para T4 (integracion AgentApiClient).
          En V1 stepper-flow el quiz se renderiza solo si se pasa <code>question</code> + <code>options</code> directamente en stepData.
        </p>
      </div>
    `;
    stepCtx.recordResult({ skipped: true, reason: 'pending-T4-fetch', skillCode });
    return;
  }

  if (!question || options.length === 0) {
    container.innerHTML = `
      <div style="padding:14px;color:#f85149;font-size:0.875rem">
        quiz-embedded: falta <code>question</code> o <code>options</code>
      </div>
    `;
    stepCtx.recordResult({ skipped: true, reason: 'invalid-data' });
    return;
  }

  const optionsHtml = options.map(o => `
    <button type="button" class="aa-quiz-option" data-aa-quiz-option-id="${stepCtx.escHtml(o.id)}"
            style="display:block;width:100%;text-align:left;padding:10px 14px;margin-bottom:8px;
                   background:#0d1117;border:1px solid #30363d;border-radius:6px;color:#c9d1d9;
                   cursor:pointer;font-size:0.88rem;line-height:1.4;transition:background 0.12s, border-color 0.12s">
      ${stepCtx.escHtml(o.text)}
    </button>
  `).join('');

  container.innerHTML = `
    <div class="aa-step-quiz" data-aa-substep-type="quiz-embedded">
      <div style="margin-bottom:12px">
        <span style="display:inline-block;padding:2px 8px;background:#1f6feb22;color:#79c0ff;border-radius:10px;font-size:0.875rem;letter-spacing:0.04em;text-transform:uppercase">Quiz</span>
        <h4 style="margin:8px 0 0;color:#f0f6fc;font-size:0.98rem;line-height:1.4">${stepCtx.escHtml(question)}</h4>
      </div>
      <div class="aa-quiz-options">${optionsHtml}</div>
      <div data-aa-quiz-feedback style="display:none;margin-top:12px;padding:10px 12px;border-radius:6px;font-size:0.875rem"></div>
    </div>
  `;

  const $optionBtns = container.querySelectorAll('.aa-quiz-option');
  const $feedback = container.querySelector('[data-aa-quiz-feedback]');
  let answered = false;

  $optionBtns.forEach(btn => {
    btn.addEventListener('mouseover', () => {
      if (!answered) btn.style.borderColor = '#58a6ff';
    });
    btn.addEventListener('mouseout', () => {
      if (!answered) btn.style.borderColor = '#30363d';
    });
    btn.addEventListener('click', () => {
      if (answered && !allowRetry) return;
      const selectedId = btn.dataset.aaQuizOptionId;
      const isCorrect = correctId !== null && selectedId === correctId;

      // Resaltar la respuesta del usuario + correcta
      $optionBtns.forEach(b => {
        const id = b.dataset.aaQuizOptionId;
        if (id === selectedId) {
          b.style.background = isCorrect ? '#23863633' : '#f8514933';
          b.style.borderColor = isCorrect ? '#3fb950' : '#f85149';
        } else if (id === correctId && !isCorrect) {
          b.style.background = '#23863622';
          b.style.borderColor = '#3fb950';
        }
        b.disabled = true;
        b.style.cursor = 'default';
      });

      $feedback.style.display = 'block';
      $feedback.style.background = isCorrect ? '#23863622' : '#f8514922';
      $feedback.style.border = `1px solid ${isCorrect ? '#3fb950' : '#f85149'}`;
      $feedback.style.color = isCorrect ? '#3fb950' : '#ffa198';
      $feedback.innerHTML = `
        <strong>${isCorrect ? '✓ Correcto' : '✗ Incorrecto'}</strong>
        ${explanation ? `<div style="margin-top:4px;color:#c9d1d9;font-size:0.875rem;line-height:1.45">${stepCtx.escHtml(explanation)}</div>` : ''}
        ${!isCorrect && allowRetry && !answered ? '<div style="margin-top:6px;font-size:0.875rem;color:#8b949e">Podes reintentar.</div>' : ''}
      `;

      stepCtx.recordResult({ answered: true, selectedId, correct: isCorrect });

      if (isCorrect || !allowRetry) {
        answered = true;
        // Re-habilitar boton de avance ya esta delegado al stepper (recordResult dispara update)
      } else {
        // Retry: limpiar tras 1.5s
        setTimeout(() => {
          $optionBtns.forEach(b => {
            b.disabled = false;
            b.style.cursor = 'pointer';
            b.style.background = '#0d1117';
            b.style.borderColor = '#30363d';
          });
          $feedback.style.display = 'none';
        }, 1500);
      }
    });
  });
}
